import { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, MicOff, Wifi, WifiOff } from 'lucide-react';

const FADER_CC = 15; // CC15 = Fader 1 on RodeCaster
const FADER_THRESHOLD = 10; // Below this = mic off (0-127 range)

const MicLiveIndicator = () => {
  const [micLive, setMicLive] = useState(false);
  const [midiConnected, setMidiConnected] = useState(false);
  const [faderValue, setFaderValue] = useState(0);
  const midiAccessRef = useRef(null);

  const handleMidiMessage = useCallback((event) => {
    const [status, cc, value] = event.data;
    // CC messages: 0xB0-0xBF (channels 1-16)
    if ((status & 0xF0) === 0xB0 && cc === FADER_CC) {
      setFaderValue(value);
      setMicLive(value > FADER_THRESHOLD);
    }
  }, []);

  const connectMidi = useCallback(async () => {
    if (!navigator.requestMIDIAccess) return;

    try {
      const access = await navigator.requestMIDIAccess({ sysex: false });
      midiAccessRef.current = access;

      const attachListeners = () => {
        let hasDevice = false;
        access.inputs.forEach((input) => {
          input.onmidimessage = handleMidiMessage;
          hasDevice = true;
        });
        setMidiConnected(hasDevice);
      };

      attachListeners();

      // Re-attach when devices connect/disconnect
      access.onstatechange = () => {
        attachListeners();
      };
    } catch (err) {
      console.warn('Web MIDI not available:', err);
      setMidiConnected(false);
    }
  }, [handleMidiMessage]);

  useEffect(() => {
    connectMidi();
    return () => {
      if (midiAccessRef.current) {
        midiAccessRef.current.inputs.forEach((input) => {
          input.onmidimessage = null;
        });
      }
    };
  }, [connectMidi]);

  // Don't render if Web MIDI isn't supported
  if (!navigator.requestMIDIAccess) return null;

  return (
    <div
      className={`mic-live-banner ${micLive ? 'live' : 'off'} ${!midiConnected ? 'disconnected' : ''}`}
      data-testid="mic-live-banner"
    >
      <div className="mic-live-inner">
        {midiConnected ? (
          <>
            <div className={`mic-live-dot ${micLive ? 'pulse' : ''}`} />
            {micLive ? <Mic size={22} /> : <MicOff size={22} />}
            <span className="mic-live-text">
              {micLive ? 'MIC LIVE' : 'MIC OFF'}
            </span>
            <div className="mic-fader-bar">
              <div className="mic-fader-fill" style={{ width: `${(faderValue / 127) * 100}%` }} />
            </div>
          </>
        ) : (
          <>
            <WifiOff size={18} />
            <span className="mic-live-text-small">No MIDI device — connect RodeCaster via USB</span>
          </>
        )}
      </div>
    </div>
  );
};

export default MicLiveIndicator;
